/**
 * Stitch's "Create New Batch from Effective MBR" (POST /batches, permission batch.create). Only templates that
 * have an EFFECTIVE version are offered (WF-019); the server re-checks that guard and the batch-number
 * uniqueness (WF-021) and its refusal is shown in the dialog. Left blank, the batch number is allocated by the
 * server's site sequence.
 */
import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { commandId, endpoints, refusalMessage } from "../lib/api.js";
import { masterData } from "../lib/master-data-api.js";
import { ActionDialog, DIALOG_FIELD, DIALOG_LABEL } from "./ActionDialog.js";

const QTY = /^\d{1,29}(\.\d{1,9})?$/; // the API's plannedQty schema

export function CreateBatchDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const templates = useQuery({ queryKey: ["mbr-templates"], queryFn: () => endpoints.mbrTemplates() });
  const active = (templates.data ?? []).filter((t) => t.is_active);
  const versions = useQueries({
    queries: active.map((t) => ({
      queryKey: ["mbr-versions", t.id],
      queryFn: () => endpoints.mbrVersions(t.id),
    })),
  });
  const effective = active.filter((_, i) => versions[i]?.data?.some((v) => v.status === "EFFECTIVE"));
  const lookups = useQuery({ queryKey: ["md-lookups"], queryFn: () => masterData.lookups() });

  const [templateId, setTemplateId] = useState("");
  const [plannedQty, setPlannedQty] = useState("");
  const [uomCode, setUomCode] = useState("");
  const [batchNumber, setBatchNumber] = useState("");
  const [erpOrderRef, setErpOrderRef] = useState("");
  const [refusal, setRefusal] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      endpoints.createBatch({
        clientCommandId: commandId(),
        templateId,
        plannedQty: plannedQty.trim(),
        uomCode,
        ...(batchNumber.trim() ? { batchNumber: batchNumber.trim() } : {}),
        ...(erpOrderRef.trim() ? { erpOrderRef: erpOrderRef.trim() } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["batches"] });
      onClose();
    },
    onError: (e: unknown) => setRefusal(refusalMessage(e)),
  });

  const ready = templateId !== "" && uomCode !== "" && QTY.test(plannedQty.trim());
  return (
    <ActionDialog
      title="Create batch from an effective MBR"
      icon="add"
      confirmLabel="Create batch"
      confirmDisabled={!ready}
      pending={create.isPending}
      refusal={refusal}
      onConfirm={() => create.mutate()}
      onClose={onClose}
    >
      <div>
        <label className={DIALOG_LABEL} htmlFor="cb-template">
          Master batch record
        </label>
        <select
          id="cb-template"
          className={DIALOG_FIELD}
          value={templateId}
          onChange={(e) => setTemplateId(e.target.value)}
        >
          <option value="">
            {effective.length ? "Select a master batch record" : "No effective master batch record"}
          </option>
          {effective.map((t) => (
            <option key={t.id} value={t.id}>{`${t.code} · ${t.name}`}</option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={DIALOG_LABEL} htmlFor="cb-qty">
            Planned quantity
          </label>
          <input
            id="cb-qty"
            inputMode="decimal"
            className={`${DIALOG_FIELD} font-mono`}
            value={plannedQty}
            onChange={(e) => setPlannedQty(e.target.value)}
          />
        </div>
        <div>
          <label className={DIALOG_LABEL} htmlFor="cb-uom">
            Unit of measure
          </label>
          <select
            id="cb-uom"
            className={DIALOG_FIELD}
            value={uomCode}
            onChange={(e) => setUomCode(e.target.value)}
          >
            <option value="">Select</option>
            {(lookups.data?.uoms ?? []).map((u) => (
              <option key={u.code} value={u.code}>
                {u.code}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={DIALOG_LABEL} htmlFor="cb-number">
            Batch number (optional)
          </label>
          <input
            id="cb-number"
            className={`${DIALOG_FIELD} font-mono`}
            value={batchNumber}
            onChange={(e) => setBatchNumber(e.target.value)}
          />
        </div>
        <div>
          <label className={DIALOG_LABEL} htmlFor="cb-erp">
            ERP order reference (optional)
          </label>
          <input
            id="cb-erp"
            className={`${DIALOG_FIELD} font-mono`}
            value={erpOrderRef}
            onChange={(e) => setErpOrderRef(e.target.value)}
          />
        </div>
      </div>
    </ActionDialog>
  );
}
